/**
 * Sidebar Component
 *
 * Side navigation for main app sections.
 */

import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  FileText,
  DollarSign,
  User,
  Settings,
  Shield,
  Users,
  BarChart3,
  X,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/store/authStore';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const isAdmin = user?.role === 'admin';

  const navigation = [
    { name: t('navigation.dashboard'), href: '/dashboard', icon: LayoutDashboard },
    { name: t('navigation.myProjects'), href: '/content', icon: FileText },
    { name: 'Total Sales', href: '/sales', icon: DollarSign },
    { name: t('navigation.profile'), href: '/profile', icon: User },
  ];

  const adminNavigation = [
    { name: 'Admin Dashboard', href: '/admin', icon: Shield },
    { name: t('navigation.manageUsers'), href: '/admin/users', icon: Users },
    { name: t('navigation.manageSales'), href: '/admin/content', icon: BarChart3 },
    { name: t('navigation.bookSales'), href: '/admin/sales', icon: DollarSign },
  ];

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.href;
    return (
      <Link
        to={item.href}
        onClick={onClose}
        className={cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        )}
      >
        <item.icon className="h-4 w-4" />
        {item.name}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-16 z-40 h-[calc(100vh-4rem)] w-64 border-r bg-background transition-transform duration-300 lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col gap-4 p-4">
          {/* Close button for mobile */}
          <div className="flex items-center justify-between lg:hidden">
            <span className="font-semibold">{t('navigation.menu')}</span>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Main navigation */}
          <nav className="flex flex-col gap-1">
            <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              {t('navigation.main')}
            </p>
            {navigation.map((item) => (
              <NavLink key={item.href} item={item} />
            ))}
          </nav>

          {/* Admin navigation */}
          {isAdmin && (
            <nav className="flex flex-col gap-1 mt-4 pt-4 border-t">
              <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                {t('navigation.admin')}
              </p>
              {adminNavigation.map((item) => (
                <NavLink key={item.href} item={item} />
              ))}
            </nav>
          )}

          {/* Bottom section */}
          <div className="mt-auto">
            <div className="rounded-lg bg-muted p-4">
              <p className="text-sm font-medium">Need help?</p>
              <p className="text-xs text-muted-foreground mt-1">
                Check our documentation for guides and tutorials.
              </p>
              <Button variant="secondary" size="sm" className="mt-3 w-full">
                View Docs
              </Button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
